"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { ActionMenuAction, ActionMenuSection } from "./actionMenu.types";

interface ActionMenuContribution {
  owner: string;
  quickActions?: ActionMenuAction[];
  featureSections?: ActionMenuSection[];
  utilityActions?: ActionMenuAction[];
}

interface ActionMenuSlotsValue {
  quickActions: ActionMenuAction[];
  featureSections: ActionMenuSection[];
  utilityActions: ActionMenuAction[];
}

interface ActionMenuContributionRegistryValue {
  setContribution: (contribution: ActionMenuContribution) => void;
  clearContribution: (owner: string) => void;
}

const ActionMenuSlotsContext = createContext<ActionMenuSlotsValue | undefined>(
  undefined,
);
const ActionMenuContributionRegistryContext = createContext<
  ActionMenuContributionRegistryValue | undefined
>(undefined);

export function ActionMenuContributionProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [contributions, setContributions] = useState<ActionMenuContribution[]>(
    [],
  );

  const setContribution = useCallback((contribution: ActionMenuContribution) => {
      setContributions((current) => [
        ...current.filter((item) => item.owner !== contribution.owner),
        contribution,
      ]);
    },
    [],
  );

  const clearContribution = useCallback((owner: string) => {
    setContributions((current) =>
      current.filter((contribution) => contribution.owner !== owner),
    );
  }, []);

  const activeContribution = contributions.at(-1) ?? null;

  const slotsValue = useMemo(
    () => ({
      quickActions: activeContribution?.quickActions ?? [],
      featureSections: activeContribution?.featureSections ?? [],
      utilityActions: activeContribution?.utilityActions ?? [],
    }),
    [activeContribution],
  );

  const registryValue = useMemo(
    () => ({
      setContribution,
      clearContribution,
    }),
    [setContribution, clearContribution],
  );

  return (
    <ActionMenuContributionRegistryContext.Provider value={registryValue}>
      <ActionMenuSlotsContext.Provider value={slotsValue}>
        {children}
      </ActionMenuSlotsContext.Provider>
    </ActionMenuContributionRegistryContext.Provider>
  );
}

function useActionMenuContributionRegistry() {
  const context = useContext(ActionMenuContributionRegistryContext);

  if (!context) {
    throw new Error(
      "useActionMenuContributionRegistry must be used within ActionMenuContributionProvider",
    );
  }

  return context;
}

export function useActionMenuContribution({
  owner,
  quickActions,
  featureSections,
  utilityActions,
}: ActionMenuContribution) {
  const { setContribution, clearContribution } =
    useActionMenuContributionRegistry();

  useEffect(() => {
    if (
      (quickActions?.length ?? 0) === 0 &&
      (featureSections?.length ?? 0) === 0 &&
      (utilityActions?.length ?? 0) === 0
    ) {
      clearContribution(owner);
      return;
    }

    setContribution({
      owner,
      quickActions,
      featureSections,
      utilityActions,
    });

    return () => {
      clearContribution(owner);
    };
  }, [
    owner,
    quickActions,
    featureSections,
    utilityActions,
    setContribution,
    clearContribution,
  ]);
}

export function useActionMenuSlots() {
  const context = useContext(ActionMenuSlotsContext);

  if (!context) {
    throw new Error(
      "useActionMenuSlots must be used within ActionMenuContributionProvider",
    );
  }

  return context;
}
